"use client"

import { useEffect } from "react"

import { useGamingHub } from "../../providers/GamingHubProvider"

/**
 * The hosted chrome script decides signed-in vs anonymous from the Sun SSO
 * cookie `acs_ngn`, which this app never sets — it authenticates through the
 * Gaming Hub web component instead. This bridge re-applies the header's own
 * show/hide rules from the Gaming Hub auth state and points its sign-in and
 * log-out affordances at the component's flows.
 *
 * The display values mirror the ones the hosted stylesheet expects, since the
 * script toggles inline styles rather than classes.
 *
 * Everything here is re-applied whenever the header subtree changes. The hosted
 * stylesheet ships `.headerLogin, .headerProfile { display: none }`, so the
 * header has no account controls at all until JavaScript un-hides one of them —
 * and React re-creates that subtree from scratch on any RSC refresh that
 * changes the fetched HTML, which drops our inline styles along with the hosted
 * script's one-shot `initializeMenu()` work.
 */
const VISIBLE_WHEN_AUTHENTICATED: Record<string, string> = {
  headerProfile: "flex",
  myProfile: "block",
  profileItem: "flex",
  logoutItem: "block",
}

/**
 * Marks header markup we have already handed to `initializeMenu()`. It goes on
 * a node from the hosted HTML rather than on the container React owns, because
 * a re-render replaces that container's `innerHTML` and leaves the container —
 * and anything we set on it — in place.
 */
const INITIALISED_ATTRIBUTE = "data-dt-chrome-init"

function setDisplay(id: string, value: string) {
  const node = document.getElementById(id)
  if (node) node.style.display = value
}

/**
 * `initGamesHubMenus()` appends to the header and footer nav containers, and
 * the clone step copies the header container — id and all — into `.menuRow`.
 * Re-running it therefore has to start from empty containers, or the half of
 * the chrome React left alone ends up with its items twice.
 */
function resetHostedMenu() {
  document
    .querySelectorAll(".menuRow .menuItemsSource")
    .forEach((clone) => clone.remove())
  document
    .querySelectorAll("#headerGamesNav, #footerGamesNav")
    .forEach((nav) => nav.replaceChildren())
}

export default function ChromeAuthBridge() {
  const { status, login, logout } = useGamingHub()

  // Delegated from the document so the handlers survive header re-creation,
  // which would otherwise leave them bound to detached nodes.
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target
      if (!(target instanceof Element)) return

      if (target.closest("#headerLogin")) {
        event.preventDefault()
        login()
        return
      }

      if (target.closest("#headerLogout")) {
        event.preventDefault()
        logout()
      }
    }

    document.addEventListener("click", onClick)
    return () => document.removeEventListener("click", onClick)
  }, [login, logout])

  useEffect(() => {
    const apply = () => {
      const loginLink = document.getElementById("headerLogin")
      const logoutLink = document.getElementById("headerLogout")

      if (loginLink && !loginLink.hasAttribute(INITIALISED_ATTRIBUTE)) {
        loginLink.setAttribute(INITIALISED_ATTRIBUTE, "1")
        // On first load the script has not run yet and bootstraps itself, so we
        // only step in for markup it will never see.
        const initializeMenu = window.components?.initializeMenu
        if (initializeMenu) {
          resetHostedMenu()
          void initializeMenu()
        }
      }

      // Neutralise the hrefs so a middle-click or dragged link cannot bypass
      // the delegated handlers above. `initializeMenu()` sets them to the
      // thesun.co.uk OIDC endpoints, both on load and on every re-run.
      if (loginLink instanceof HTMLAnchorElement) loginLink.href = "#"
      if (logoutLink instanceof HTMLAnchorElement) logoutLink.href = "#"

      if (status === "loading") return

      const authenticated = status === "authenticated"
      setDisplay("headerLogin", authenticated ? "none" : "flex")
      for (const [id, shown] of Object.entries(VISIBLE_WHEN_AUTHENTICATED)) {
        setDisplay(id, authenticated ? shown : "none")
      }
    }

    apply()

    // Only childList is observed, so the styles and hrefs written above do not
    // feed back into the observer. `initializeMenu()` finishes asynchronously,
    // and that append is what re-triggers us after a re-init.
    let frame = 0
    const schedule = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        apply()
      })
    }

    const observer = new MutationObserver(schedule)
    observer.observe(document.body, { childList: true, subtree: true })
    window.addEventListener("load", apply)

    return () => {
      observer.disconnect()
      window.removeEventListener("load", apply)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [status])

  return null
}
